import { useCallback, useEffect, useMemo, useState } from 'react'

import { MAX_LIMIT_PAGE_SIZE_NEAR_BY_LOCATION } from 'constant'
import { useSearch } from 'contexts/SearchProvider'
import { Location } from 'features/common/types'
import { useGetLocationsQuery } from 'store/booking/api'
import { selectBooking } from 'store/booking/slice'
import { useAppSelector } from 'store/store'

export interface IUseLocations {
  isLoading: boolean
  locations: Location[]
  hasNextPage: boolean
  loadMore: () => void
  onSearch: (value: string) => void
  isNearby: boolean
  enableNearby: (value: boolean) => void
}

export const useLocations = (): IUseLocations => {
  const [isNearby, setIsNearby] = useState<boolean>(false)
  const [search, setSearch] = useState<string | undefined>()
  const [debouncedSearch, setDebouncedSearch] = useState<string | undefined>()
  const [page, setPage] = useState<number>(1)
  const { locations } = useAppSelector(selectBooking)
  const { myLocation } = useSearch()

  const { data, isFetching } = useGetLocationsQuery(
    {
      q: debouncedSearch,
      page,
      page_size: isNearby ? MAX_LIMIT_PAGE_SIZE_NEAR_BY_LOCATION : 15,
      lat: isNearby ? myLocation?.lat : undefined,
      lng: isNearby ? myLocation?.lng : undefined,
    },
    { refetchOnMountOrArgChange: true },
  )

  const hasNextPage = useMemo(() => {
    if (!data) return false
    if (data.page_size !== data.items.length) return false
    return locations.length < data.total_items
  }, [data, locations.length])

  const loadMore = useCallback(() => {
    if (hasNextPage) {
      setPage(page + 1)
    }
  }, [hasNextPage, page])

  const onSearch = (value: string) => {
    setPage(1)
    setSearch(value)
  }

  const enableNearby = useCallback(
    (value: boolean) => {
      if (myLocation) {
        setIsNearby(value)
      }
    },
    [myLocation],
  )

  useEffect(() => {
    setPage(1)
  }, [isNearby])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)
    return () => {
      clearTimeout(handler)
    }
  }, [search])

  return {
    isLoading: isFetching,
    locations,
    hasNextPage,
    loadMore,
    onSearch,
    isNearby,
    enableNearby,
  }
}
